import { useState, useMemo } from 'react';
import KpiCard from '@/components/dashboard/KpiCard';
import ChartCard from '@/components/dashboard/ChartCard';
import useDashboardV2 from '@/hooks/useDashboardV2';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Legend,
} from 'recharts';
import { Megaphone, Target, TrendingUp, Sparkles, Users, Layers, DollarSign, Activity, BarChart3, Clock, Repeat } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PALETTE = ['#A5D62C', '#1B9CEA', '#F59E0B', '#A855F7', '#EC4899', '#14B8A6', '#3B82F6', '#10B981'];
const TAB_KEY = 'marketing';

const VERTICAL_LABELS = {
  real_estate: { lead: 'Lead Inmobiliarios', leadLow: 'lead inmobiliarios', source: 'Origen del lead inmobiliario' },
  developments: { lead: 'Leads de Desarrollo', leadLow: 'leads de desarrollo', source: 'Origen del lead' },
  automotive: { lead: 'Lead Automotrices', leadLow: 'lead automotrices', source: 'Origen del prospecto' },
  education: { lead: 'Aspirantes', leadLow: 'aspirantes', source: 'Canal de captación' },
  generic: { lead: 'Leads', leadLow: 'leads', source: 'Origen del lead' },
};

const EMPTY = 'Sin datos para el rango seleccionado';
const TOOLTIP_STYLE = { background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11, color: '#fff' };

// Fix 6.1 / 6.4-Marketing: formatea segundos a una unidad legible.
const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};

export default function MarketingTab({ filters, vertical = 'generic' }) {
  const labels = VERTICAL_LABELS[vertical] || VERTICAL_LABELS.generic;
  const [selectedCampaignId, setSelectedCampaignId] = useState('all');

  const kpisQ = useDashboardV2({ endpoint: 'kpis', filters, vertical });
  const chartsQ = useDashboardV2({ endpoint: 'charts', filters, vertical });
  const trendsQ = useDashboardV2({ endpoint: 'trends', filters, vertical });
  const deepQ = useDashboardV2({ endpoint: 'marketing-deep', filters, vertical });
  // Fix 6.1: Tiempo de Reaccion Comercial -- respeta los filtros del header (fecha/vendedor/grupo/campana/fuente).
  const reactionQ = useDashboardV2({ endpoint: 'reaction-time', filters, vertical });
  // Fix 6.2: presupuesto solo de campanas activas -- respeta fecha del header para contar leads del periodo.
  const budgetQ = useDashboardV2({ endpoint: 'campaign-budget', filters, vertical });
  // Fix 6.4-Marketing: tiempo entre etapas de leads.status -- deliberadamente SIN filtros del
  // header (pedido explicito), solo el par de etapas a comparar.
  const stageTimeQ = useDashboardV2({ endpoint: 'lead-stage-time' });
  // Fix (feedback owner jul2026): historico de 12 meses, independiente de los filtros del
  // header -- un presupuesto de un mes pasado no depende del rango de fecha seleccionado arriba.
  const budgetHistoryQ = useDashboardV2({ endpoint: 'campaign-budget-history', extra: { months_back: 12 } });

  const core = kpisQ.data?.core || {};
  const sourcesObj = chartsQ.data?.leads_by_source || {};
  const sourceData = Object.entries(sourcesObj).map(([name, value]) => ({ name, value }));
  const monthly = trendsQ.data?.monthly || [];
  const newLeads = core.new_leads ?? 0;
  const totalLeads = core.total_leads ?? 0;
  // dashboard-pipeline P0-E: renombrado de "Tasa de Captura" — el cálculo real es
  // (nuevo+prospecto) / total del filtro, casi siempre ~90%+ porque casi todos los
  // leads recién creados están en etapa inicial. No mide captación de mercado; mide
  // cuántos siguen sin avanzar de etapa.
  const initialStageRate = totalLeads > 0 ? Math.round((newLeads / totalLeads) * 100) : 0;

  const byCampaign = deepQ.data?.leads_by_campaign || [];
  const byGroup = deepQ.data?.leads_by_group || [];
  const channelCampaignGrouped = useMemo(
    () => deepQ.data?.channel_campaign_grouped || [],
    [deepQ.data],
  );
  const cplByChannel = deepQ.data?.cpl_by_channel || [];
  const campaignHasBudget = deepQ.data?.campaign_has_budget || false;
  const daily90 = deepQ.data?.daily_90 || [];
  const campaignsCatalog = deepQ.data?.campaigns_catalog || [];

  // Para el chart "Leads por canal + campaña": pivotamos a stacked
  const pivotChannelCampaign = useMemo(() => {
    const channels = [...new Set(channelCampaignGrouped.map(r => r.channel))];
    const campaigns = [...new Set(channelCampaignGrouped.map(r => r.campaign))].slice(0, 6);
    return channels.map(ch => {
      const row = { channel: ch };
      campaigns.forEach(camp => {
        const match = channelCampaignGrouped.find(r => r.channel === ch && r.campaign === camp);
        row[camp] = match ? match.count : 0;
      });
      return row;
    });
  }, [channelCampaignGrouped]);
  const campaignKeys = useMemo(() => (
    [...new Set(channelCampaignGrouped.map(r => r.campaign))].slice(0, 6)
  ), [channelCampaignGrouped]);

  // Pivot del historico de presupuesto: barra apilada por mes, top 6 campañas por presupuesto acumulado.
  const budgetHistoryMonths = budgetHistoryQ.data?.months || [];
  const budgetTopCampaigns = useMemo(() => {
    const totals = {};
    budgetHistoryMonths.forEach(m => (m.by_campaign || []).forEach(bc => {
      totals[bc.name] = (totals[bc.name] || 0) + bc.amount;
    }));
    return Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name]) => name);
  }, [budgetHistoryMonths]);
  const budgetHistoryPivot = useMemo(() => budgetHistoryMonths.map(m => {
    const row = { month: m.month, total: m.total };
    budgetTopCampaigns.forEach(name => {
      const match = (m.by_campaign || []).filter(bc => bc.name === name).reduce((s, bc) => s + bc.amount, 0);
      row[name] = match;
    });
    return row;
  }), [budgetHistoryMonths, budgetTopCampaigns]);

  // Para "Leads por campaña (seleccionable)"
  const filteredByCampaign = selectedCampaignId === 'all'
    ? byCampaign
    : byCampaign.filter(c => c.name === (campaignsCatalog.find(cc => cc.id === selectedCampaignId)?.name));

  return (
    <div className="space-y-6" data-testid="tab-marketing-content">
      {/* KPIs -- fila unica, scroll horizontal en movil (feedback owner jul2026) */}
      <div className="flex overflow-x-auto gap-3 pb-1 sm:grid sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard icon={Megaphone} label={labels.lead} value={kpisQ.loading ? '—' : totalLeads} sub={`${newLeads} nuevos`} color="cyan" loading={kpisQ.loading} testid="mkt-kpi-leads" />
        <KpiCard icon={Sparkles} label="En etapa inicial" value={kpisQ.loading ? '—' : `${initialStageRate}%`} sub="nuevo + prospecto / total" color="violet" loading={kpisQ.loading} testid="mkt-kpi-capture" />
        <KpiCard icon={Target} label="Conversión Global" value={kpisQ.loading ? '—' : `${core.conversion_rate ?? 0}%`} sub={`${core.won_leads ?? 0} ganados`} color="emerald" loading={kpisQ.loading} testid="mkt-kpi-conv" />
        <KpiCard icon={Users} label="Fuentes Activas" value={chartsQ.loading ? '—' : sourceData.length} sub="canales con leads" color="amber" loading={chartsQ.loading} testid="mkt-kpi-sources" />
        <KpiCard
          icon={Clock}
          label="Reacción Comercial (mediana)"
          value={reactionQ.loading ? '—' : formatDuration(reactionQ.data?.reaction_seconds_median)}
          sub={reactionQ.data
            ? `mediana de ${reactionQ.data.n_total ?? 0} · ${reactionQ.data.abandonadas_24h ?? 0} sin atender +24h · ${reactionQ.data.calls_breakdown?.contestadas ?? 0} llamadas contestadas`
            : 'IA → primera reacción del vendedor'}
          color="cyan"
          loading={reactionQ.loading}
          testid="mkt-kpi-reaction-time"
        />
        <KpiCard
          icon={Repeat}
          label="Tiempo promedio entre etapas"
          value={stageTimeQ.loading ? '—' : formatDuration(stageTimeQ.data?.avg_seconds_between_any_stage_change)}
          sub={`${stageTimeQ.data?.n_transitions ?? 0} transiciones · ${(budgetQ.data?.campaigns || []).filter(c => c.budget != null).length}/${(budgetQ.data?.campaigns || []).length} campañas con presupuesto`}
          color="violet"
          loading={stageTimeQ.loading}
          testid="mkt-kpi-stage-time"
        />
      </div>

      {/* Origen del lead (barras) + Captación mensual */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={labels.source} icon={Megaphone} loading={chartsQ.loading} empty={!sourceData.length} emptyMessage={EMPTY} testid="mkt-chart-origin-bar" downloadable tabKey={TAB_KEY} chartId="mkt-chart-origin-bar" chartData={sourceData}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={sourceData} margin={{ left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
              <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {sourceData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={`Captación mensual de ${labels.leadLow}`} icon={TrendingUp} loading={trendsQ.loading} empty={!monthly.length} emptyMessage={EMPTY} testid="mkt-chart-monthly" downloadable tabKey={TAB_KEY} chartId="mkt-chart-monthly">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly} margin={{ left: 8, bottom: 14 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Mes', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="leads" fill="#1B9CEA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Leads por fuente (pastel) + Leads por canal + campaña */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Leads por fuente" icon={Activity} loading={chartsQ.loading} empty={!sourceData.length} emptyMessage={EMPTY} testid="mkt-chart-source-pie" downloadable tabKey={TAB_KEY} chartId="mkt-chart-source-pie" chartData={sourceData}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2} label={{ fill: '#cbd5e1', fontSize: 10 }}>
                {sourceData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por canal + campaña" icon={BarChart3} loading={deepQ.loading} empty={!pivotChannelCampaign.length} emptyMessage={EMPTY} testid="mkt-chart-channel-campaign" downloadable tabKey={TAB_KEY} chartId="mkt-chart-channel-campaign">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pivotChannelCampaign} margin={{ left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="channel" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
              <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {campaignKeys.map((camp, i) => (
                <Bar key={camp} dataKey={camp} stackId="a" fill={PALETTE[i % PALETTE.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Leads por campaña (seleccionable) + Leads por Grupos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Leads por campaña"
          icon={Megaphone}
          loading={deepQ.loading}
          empty={!filteredByCampaign.length}
          emptyMessage={EMPTY}
          testid="mkt-chart-campaign"
          downloadable
          tabKey={TAB_KEY}
          chartId="mkt-chart-campaign"
          chartData={filteredByCampaign}
          actions={
            <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
              <SelectTrigger className="h-7 bg-black/20 border-white/10 text-[10px] w-44" data-testid="mkt-campaign-select">
                <SelectValue placeholder="Todas las campañas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">Todas las campañas</SelectItem>
                {campaignsCatalog.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={filteredByCampaign} layout="vertical" margin={{ left: 80, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis dataKey="name" type="category" tick={{ fill: '#8893A7', fontSize: 9 }} axisLine={false} width={110} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {filteredByCampaign.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads por Grupos" icon={Layers} loading={deepQ.loading} empty={!byGroup.length} emptyMessage={EMPTY} testid="mkt-chart-groups" downloadable tabKey={TAB_KEY} chartId="mkt-chart-groups" chartData={byGroup}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byGroup} margin={{ left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
              <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {byGroup.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* CPL por canal */}
      <ChartCard
        title="CPL por canal (presupuesto)"
        icon={DollarSign}
        loading={deepQ.loading}
        empty={!cplByChannel.length || !campaignHasBudget}
        emptyMessage={campaignHasBudget ? EMPTY : 'Para calcular CPL agrega presupuesto a tus campañas (campaign.budget)'}
        testid="mkt-chart-cpl"
        downloadable
        tabKey={TAB_KEY}
        chartId="mkt-chart-cpl"
      >
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={cplByChannel} margin={{ left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="channel" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis yAxisId="left" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={v => `$${v}`} label={{ value: 'CPL ($)', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: 90, position: 'insideRight', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n) => n === 'cpl' ? [`$${v}`, 'CPL'] : [v, n === 'leads' ? 'Leads' : n]} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar yAxisId="left" dataKey="cpl" name="CPL ($)" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="right" dataKey="leads" name="Leads" fill="#1B9CEA" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Lineal últimos 90 días: únicos / duplicados / totales */}
      <ChartCard
        title="Últimos 90 días: únicos · duplicados · totales"
        icon={TrendingUp}
        loading={deepQ.loading}
        empty={!daily90.length}
        emptyMessage={EMPTY}
        testid="mkt-chart-daily90"
        height={320}
        downloadable
        tabKey={TAB_KEY}
        chartId="mkt-chart-daily90"
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={daily90} margin={{ left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" tick={{ fill: '#8893A7', fontSize: 9 }} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line type="monotone" dataKey="total" name="Totales" stroke="#1B9CEA" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="unique" name="Únicos" stroke="#10B981" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="duplicates" name="Duplicados" stroke="#EF4444" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Fix 6.2/6.3: Presupuesto por campaña activa */}
      <ChartCard
        title="Presupuesto por campaña activa"
        icon={DollarSign}
        loading={budgetQ.loading}
        empty={!(budgetQ.data?.campaigns || []).length}
        emptyMessage={EMPTY}
        testid="mkt-table-budget"
        tabKey={TAB_KEY}
        chartId="mkt-table-budget"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-white/5">
                <th className="py-2 px-3 font-normal">Campaña</th>
                <th className="py-2 px-3 font-normal">Canal</th>
                <th className="py-2 px-3 font-normal text-right">Presupuesto</th>
                <th className="py-2 px-3 font-normal text-right">Leads</th>
                <th className="py-2 px-3 font-normal text-right">CPL</th>
              </tr>
            </thead>
            <tbody>
              {(budgetQ.data?.campaigns || []).map(c => (
                <tr key={c.campaign_id} className="border-b border-white/[0.02]" data-testid={`mkt-budget-row-${c.campaign_id}`}>
                  <td className="py-2 px-3 text-white">{c.name}</td>
                  <td className="py-2 px-3 text-muted-foreground">{c.channel || '—'}</td>
                  <td className="py-2 px-3 text-right text-amber-300">{c.budget != null ? `$${c.budget.toLocaleString('es-MX')}` : <span className="text-muted-foreground">Sin definir</span>}</td>
                  <td className="py-2 px-3 text-right text-cyan-300">{c.leads}</td>
                  <td className="py-2 px-3 text-right text-emerald-400">{c.cpl != null ? `$${c.cpl.toLocaleString('es-MX')}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground px-3 pb-2">El presupuesto mensual se captura en Configuración → Campañas.</p>
      </ChartCard>

      {/* Historico de presupuesto: barra apilada por mes, suma total + desglose por campaña */}
      <ChartCard
        title="Presupuesto histórico (últimos 12 meses)"
        icon={BarChart3}
        loading={budgetHistoryQ.loading}
        empty={!budgetHistoryPivot.some(m => m.total > 0)}
        emptyMessage="Aún no hay presupuesto capturado en ningún mes"
        testid="mkt-chart-budget-history"
        tabKey={TAB_KEY}
        chartId="mkt-chart-budget-history"
      >
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={budgetHistoryPivot} margin={{ left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={v => `$${v}`} label={{ value: 'Presupuesto ($)', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => `$${Number(v).toLocaleString('es-MX')}`} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {budgetTopCampaigns.map((name, i) => (
              <Bar key={name} dataKey={name} stackId="budget" fill={PALETTE[i % PALETTE.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-muted-foreground px-3 pb-2">La suma de cada barra es el presupuesto total del mes; los colores desglosan las campañas con mayor presupuesto acumulado.</p>
      </ChartCard>
    </div>
  );
}
